import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  Engagement, 
  TeamMemberUtilization, 
  UtilizationMatrix,
  CapacityForecast,
  TeamCapacityForecast,
  CapacityForecastPeriod,
  EngagementType,
  TeamRole
} from '../types/engagement';
import { engagementsService } from './engagementsService';
import { format, addMonths, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';

export const utilizationService = {
  // ============================================================================
  // TEAM UTILIZATION
  // ============================================================================

  /**
   * Calculate utilization for all team members
   */
  async calculateTeamUtilization(
    orgId: string, 
    dateRange?: { start: Date; end: Date }
  ): Promise<TeamMemberUtilization[]> {
    // Get all active engagements
    const engagements = await engagementsService.getEngagements(orgId, {
      statuses: ['active', 'planning']
    });

    // Build a map of user allocations
    const userMap = new Map<string, TeamMemberUtilization>();

    const now = new Date();
    const rangeStart = dateRange?.start || now;
    const rangeEnd = dateRange?.end || addMonths(now, 1);

    engagements.forEach(engagement => {
      // Process team allocations
      engagement.teamAllocations.forEach(ta => {
        // Check if allocation is within date range
        const allocationStart = new Date(ta.startDate);
        const allocationEnd = ta.endDate ? new Date(ta.endDate) : null;

        const isActive = allocationStart <= rangeEnd && 
          (allocationEnd === null || allocationEnd >= rangeStart);

        if (!isActive) return;

        if (!userMap.has(ta.userId)) {
          userMap.set(ta.userId, {
            userId: ta.userId,
            userName: ta.userName,
            avatarUrl: ta.avatarUrl,
            totalAllocation: 0,
            engagements: [],
            availableCapacity: 100,
            isOverallocated: false
          });
        }

        const user = userMap.get(ta.userId)!;
        user.totalAllocation += ta.allocationPercentage;
        user.engagements.push({
          engagementId: engagement.id,
          engagementName: engagement.name,
          engagementType: engagement.engagementType,
          allocation: ta.allocationPercentage
        });
      });

      // Process FTE allocations (Body Leasing)
      engagement.fteAllocations?.forEach(fa => {
        const allocationStart = new Date(fa.startDate);
        const allocationEnd = fa.endDate ? new Date(fa.endDate) : null;

        const isActive = allocationStart <= rangeEnd && 
          (allocationEnd === null || allocationEnd >= rangeStart);

        if (!isActive) return;

        if (!userMap.has(fa.userId)) {
          userMap.set(fa.userId, {
            userId: fa.userId,
            userName: fa.userName,
            avatarUrl: fa.avatarUrl,
            totalAllocation: 0,
            engagements: [],
            availableCapacity: 100,
            isOverallocated: false
          });
        }

        const user = userMap.get(fa.userId)!;
        user.totalAllocation += fa.ftePercentage;
        user.engagements.push({
          engagementId: engagement.id,
          engagementName: engagement.name,
          engagementType: engagement.engagementType,
          allocation: fa.ftePercentage
        });
      });
    });

    // Calculate available capacity and overallocation
    const result: TeamMemberUtilization[] = [];
    userMap.forEach(user => {
      user.availableCapacity = Math.max(0, 100 - user.totalAllocation);
      user.isOverallocated = user.totalAllocation > 100;
      result.push(user);
    });

    // Sort by allocation (highest first)
    return result.sort((a, b) => b.totalAllocation - a.totalAllocation);
  },

  /**
   * Get utilization for a single team member
   */
  async getMemberUtilization(
    orgId: string,
    userId: string, 
    dateRange?: { start: Date; end: Date }
  ): Promise<TeamMemberUtilization | null> {
    const allUtilization = await this.calculateTeamUtilization(orgId, dateRange);
    return allUtilization.find(u => u.userId === userId) || null;
  },

  /**
   * Find overallocated team members
   */
  async findOverallocatedMembers(orgId: string): Promise<TeamMemberUtilization[]> {
    const allUtilization = await this.calculateTeamUtilization(orgId);
    return allUtilization.filter(u => u.isOverallocated);
  },

  /**
   * Find team members with available capacity
   */
  async findAvailableCapacity(
    orgId: string, 
    requiredPercentage: number = 25
  ): Promise<TeamMemberUtilization[]> {
    const allUtilization = await this.calculateTeamUtilization(orgId);
    return allUtilization.filter(u => u.availableCapacity >= requiredPercentage);
  },

  // ============================================================================
  // UTILIZATION MATRIX
  // ============================================================================

  /**
   * Get utilization matrix for visualization
   */
  async getUtilizationMatrix(orgId: string): Promise<UtilizationMatrix> {
    const engagements = await engagementsService.getEngagements(orgId, {
      statuses: ['active', 'planning']
    });

    const membersMap = new Map<string, { userId: string; userName: string; avatarUrl?: string }>();
    const allocations: UtilizationMatrix['allocations'] = [];

    engagements.forEach(engagement => {
      engagement.teamAllocations.forEach(ta => {
        if (!membersMap.has(ta.userId)) {
          membersMap.set(ta.userId, {
            userId: ta.userId,
            userName: ta.userName,
            avatarUrl: ta.avatarUrl
          });
        }

        allocations.push({
          memberId: ta.userId,
          engagementId: engagement.id,
          percentage: ta.allocationPercentage,
          role: ta.role
        });
      });

      engagement.fteAllocations?.forEach(fa => {
        if (!membersMap.has(fa.userId)) {
          membersMap.set(fa.userId, {
            userId: fa.userId,
            userName: fa.userName,
            avatarUrl: fa.avatarUrl
          });
        }

        allocations.push({
          memberId: fa.userId,
          engagementId: engagement.id,
          percentage: fa.ftePercentage,
          role: 'member'
        });
      });
    });

    return {
      members: Array.from(membersMap.values()),
      engagements: engagements.map(e => ({
        engagementId: e.id,
        engagementName: e.name,
        engagementType: e.engagementType,
        customerId: e.customerId,
        customerName: e.customer?.name || e.customerNameManual
      })),
      allocations
    };
  },

  // ============================================================================
  // CAPACITY FORECAST
  // ============================================================================

  /**
   * Predict future utilization for specified number of months
   */
  async predictFutureUtilization(
    orgId: string, 
    months: number = 6
  ): Promise<TeamCapacityForecast> {
    const engagements = await engagementsService.getEngagements(orgId, {
      statuses: ['active', 'planning']
    });

    // Get all team members from engagements
    const teamMembers = new Map<string, { userId: string; userName: string; avatarUrl?: string }>();
    engagements.forEach(e => {
      e.teamAllocations.forEach(ta => {
        if (!teamMembers.has(ta.userId)) {
          teamMembers.set(ta.userId, {
            userId: ta.userId,
            userName: ta.userName,
            avatarUrl: ta.avatarUrl
          });
        }
      });
      e.fteAllocations?.forEach(fa => {
        if (!teamMembers.has(fa.userId)) {
          teamMembers.set(fa.userId, {
            userId: fa.userId,
            userName: fa.userName,
            avatarUrl: fa.avatarUrl
          });
        }
      });
    });

    const memberForecasts: CapacityForecast[] = [];
    const periodTotals: Map<string, { allocated: number; count: number }> = new Map();

    // Calculate forecast for each team member
    teamMembers.forEach(member => {
      const periods: CapacityForecastPeriod[] = [];

      for (let i = 0; i < months; i++) {
        const periodStart = startOfMonth(addMonths(new Date(), i));
        const periodEnd = endOfMonth(periodStart);
        const periodKey = format(periodStart, 'yyyy-MM');

        let allocatedCapacity = 0;
        const engagementsInPeriod: CapacityForecastPeriod['engagements'] = [];

        engagements.forEach(engagement => {
          // Check team allocations
          engagement.teamAllocations.forEach(ta => {
            if (ta.userId !== member.userId) return;

            const allocationStart = new Date(ta.startDate);
            const allocationEnd = ta.endDate ? new Date(ta.endDate) : engagement.endDate 
              ? new Date(engagement.endDate) 
              : addMonths(new Date(), 12); // Default to 1 year if no end date

            const isActiveInPeriod = allocationStart <= periodEnd && allocationEnd >= periodStart;

            if (isActiveInPeriod) {
              allocatedCapacity += ta.allocationPercentage;
              engagementsInPeriod.push({
                engagementId: engagement.id,
                engagementName: engagement.name,
                allocation: ta.allocationPercentage
              });
            }
          });

          // Check FTE allocations
          engagement.fteAllocations?.forEach(fa => {
            if (fa.userId !== member.userId) return;

            const allocationStart = new Date(fa.startDate);
            const allocationEnd = fa.endDate ? new Date(fa.endDate) : engagement.contractEndDate 
              ? new Date(engagement.contractEndDate) 
              : addMonths(new Date(), 12);

            const isActiveInPeriod = allocationStart <= periodEnd && allocationEnd >= periodStart;

            if (isActiveInPeriod) {
              allocatedCapacity += fa.ftePercentage;
              engagementsInPeriod.push({
                engagementId: engagement.id,
                engagementName: engagement.name,
                allocation: fa.ftePercentage
              });
            }
          });
        });

        periods.push({
          period: periodKey,
          allocatedCapacity,
          availableCapacity: Math.max(0, 100 - allocatedCapacity),
          engagements: engagementsInPeriod
        });

        // Update period totals
        if (!periodTotals.has(periodKey)) {
          periodTotals.set(periodKey, { allocated: 0, count: 0 });
        }
        const totals = periodTotals.get(periodKey)!;
        totals.allocated += allocatedCapacity;
        totals.count++;
      }

      memberForecasts.push({
        userId: member.userId,
        userName: member.userName,
        avatarUrl: member.avatarUrl,
        periods
      });
    });

    // Calculate team-wide periods
    const teamPeriods: TeamCapacityForecast['periods'] = [];
    const totalTeamMembers = teamMembers.size || 1;

    for (let i = 0; i < months; i++) {
      const periodStart = startOfMonth(addMonths(new Date(), i));
      const periodKey = format(periodStart, 'yyyy-MM');
      const totals = periodTotals.get(periodKey) || { allocated: 0, count: 0 };

      const totalTeamCapacity = totalTeamMembers; // In FTE
      const allocatedCapacity = totals.allocated / 100; // Convert % to FTE
      const utilizationPercentage = totalTeamMembers > 0 
        ? Math.round((allocatedCapacity / totalTeamCapacity) * 100) 
        : 0;

      teamPeriods.push({
        period: periodKey,
        totalTeamCapacity,
        allocatedCapacity: Math.round(allocatedCapacity * 100) / 100,
        availableCapacity: Math.round((totalTeamCapacity - allocatedCapacity) * 100) / 100,
        utilizationPercentage
      });
    }

    return {
      periods: teamPeriods,
      memberForecasts
    };
  },

  /**
   * Find when capacity will be available
   */
  async findNextAvailableCapacity(
    orgId: string,
    requiredCapacity: number = 1, // In FTE
    maxMonthsAhead: number = 12
  ): Promise<{ period: string; availableCapacity: number } | null> {
    const forecast = await this.predictFutureUtilization(orgId, maxMonthsAhead);

    for (const period of forecast.periods) {
      if (period.availableCapacity >= requiredCapacity) {
        return {
          period: period.period,
          availableCapacity: period.availableCapacity
        };
      }
    }

    return null;
  },

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Calculate average utilization
   */
  calculateAverageUtilization(utilizations: TeamMemberUtilization[]): number {
    if (utilizations.length === 0) return 0;
    const total = utilizations.reduce((sum, u) => sum + u.totalAllocation, 0);
    return Math.round(total / utilizations.length);
  },

  /**
   * Get utilization color based on percentage
   */
  getUtilizationColor(percentage: number): string {
    if (percentage > 100) return 'red';
    if (percentage >= 90) return 'orange';
    if (percentage >= 70) return 'green';
    if (percentage >= 40) return 'blue';
    return 'gray';
  },

  /**
   * Get utilization status label
   */
  getUtilizationStatus(percentage: number): { de: string; en: string } {
    if (percentage > 100) return { de: 'Überbucht', en: 'Overbooked' };
    if (percentage >= 90) return { de: 'Voll ausgelastet', en: 'Fully utilized' };
    if (percentage >= 70) return { de: 'Gut ausgelastet', en: 'Well utilized' };
    if (percentage >= 40) return { de: 'Teilweise verfügbar', en: 'Partially available' };
    return { de: 'Verfügbar', en: 'Available' };
  }
};
